<template>
  <div
    class="auth-view"
    data-view="login"
  >
    <div
      v-if="showResetBanner"
      class="auth-success-banner"
    >
      <icon-check
        :size="16"
        :stroke-width="2.5"
      />
      <span>{{ t('login_after_reset_banner') }}</span>
    </div>

    <h2>
      {{ t('login_title_prefix') }}<span class="grad">{{ t('login_title_grad') }}</span
      >{{ t('login_title_suffix') }}
    </h2>
    <p class="sub">{{ t('login_sub') }}</p>

    <SocialButtons
      label-key-prefix="login"
      intent="login"
      :cta-location="ctaLocation"
    />

    <div class="divider-or">
      <em>{{ t('divider_email') }}</em>
    </div>

    <div class="signup-field">
      <label
        class="field-label"
        id="login-email-label"
        >{{ t('lbl_email') }}</label
      >
      <input
        id="login-email"
        v-model="email"
        type="email"
        class="signup-input"
        aria-labelledby="login-email-label"
        :placeholder="t('ph_email')"
        inputmode="email"
        autocomplete="email"
      />
      <div
        v-if="errors.email"
        class="err"
      >
        <icon-info-circle
          :size="13"
          :stroke-width="2.4"
        />
        <span>{{ errors.email }}</span>
      </div>
    </div>

    <div class="signup-field">
      <label
        class="field-label"
        id="login-pw-label"
        >{{ t('lbl_password') }}</label
      >
      <div class="pw-wrap">
        <input
          id="login-pw"
          v-model="password"
          :type="showPw ? 'text' : 'password'"
          class="signup-input"
          aria-labelledby="login-pw-label"
          :placeholder="t('ph_login_password')"
          autocomplete="current-password"
        />
        <button
          type="button"
          class="pw-toggle"
          :aria-label="t('lbl_password')"
          @click="showPw = !showPw"
        >
          <icon-eye :size="18" />
        </button>
      </div>
      <div
        v-if="errors.password"
        class="err"
      >
        <icon-info-circle
          :size="13"
          :stroke-width="2.4"
        />
        <span>{{ errors.password }}</span>
      </div>
      <button
        type="button"
        class="forgot-link"
        @click="onForgotClick"
      >
        {{ t('forgot_link') }}
      </button>
    </div>

    <label class="v-check">
      <input
        v-model="stayLoggedIn"
        type="checkbox"
      />
      <span class="box"><icon-check /></span>
      <span>{{ t('stay_logged_in') }}</span>
    </label>

    <div
      v-if="submitError"
      class="submit-err show"
    >
      <icon-info-circle
        :size="16"
        :stroke-width="2.4"
      />
      <span>{{ submitError }}</span>
    </div>

    <button
      type="button"
      class="sb-next wide"
      :disabled="submitting"
      @click="submit"
    >
      <template v-if="submitting">
        <span
          class="spinner"
          aria-hidden="true"
        ></span>
        {{ t('btn_loading') }}
      </template>
      <template v-else>{{ t('login_btn') }} <span>→</span></template>
    </button>
    <button
      type="button"
      class="cross-link"
      @click="$emit('switch-mode', 'register')"
    >
      {{ t('cross_to_register_prefix') }}<strong>{{ t('cross_to_register_strong') }}</strong>
    </button>
  </div>
</template>

<script>
import { i18nMixin, refreshCsrfToken, EMAIL_RE } from './helpers';
import SocialButtons from './components/SocialButtons.vue';
import IconCheck from '../icons/IconCheck.vue';
import IconInfoCircle from '../icons/IconInfoCircle.vue';
import IconEye from '../icons/IconEye.vue';
import { trackEvent } from '../../../analytics/ga4';

export default {
  name: 'LoginForm',
  mixins: [i18nMixin],
  components: { SocialButtons, IconCheck, IconInfoCircle, IconEye },
  props: {
    showResetBanner: { type: Boolean, default: false },
    ctaLocation: { type: String, default: 'header' },
  },
  data() {
    return {
      email: '',
      password: '',
      stayLoggedIn: false,
      showPw: false,
      submitting: false,
      submitError: '',
      errors: {},
    };
  },
  methods: {
    onForgotClick() {
      trackEvent('password_reset_click', {});
      this.$emit('switch-mode', 'forgot');
    },
    trackFormError(errorType) {
      trackEvent('form_error', {
        form_type: 'login',
        error_type: errorType,
      });
    },
    async submit() {
      this.submitError = '';
      this.errors = {};

      const email = (this.email || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.errors = { email: this.t('login_err') };
        this.trackFormError('email_invalid');
        return;
      }
      if (!this.password) {
        this.errors = { password: this.t('login_err') };
        this.trackFormError('wrong_credentials');
        return;
      }

      this.submitting = true;
      this.$emit('submitting', true);
      await refreshCsrfToken();
      try {
        // Backend's Auth\LoginController accepts `login` (email/phone/username)
        // and merges it onto the matching field internally. Mirror LoginCard.vue's
        // payload shape — always send `login` + empty `email` — so the request
        // schema is identical to the legacy form.
        await axios.post(zRoute('login'), {
          login: email,
          email: '',
          password: this.password,
          remember: !!this.stayLoggedIn,
        });
        trackEvent('login', {
          method: 'email',
          remember_me: !!this.stayLoggedIn,
        });
        this.$emit('login-success');
        window.location.href = zRoute('home');
      } catch (err) {
        this.submitting = false;
        this.$emit('submitting', false);
        const data = err && err.response && err.response.data;

        // Backend can attach a structured `reason` for special failure modes
        // (blocked account, self-deletion grace period, etc.). Legacy
        // LoginCard.vue surfaces it as a 15s toast and, for USER_SELF_DELETE,
        // bounces the user into the password-reset flow with their email
        // pre-filled so they can reactivate. Mirror both behaviours.
        const reason = data && data.reason;
        if (reason && reason.type && reason.message) {
          this.$notify({
            group: 'custom-template',
            type: 'error',
            text: reason.message,
            duration: 15000,
            data: {},
          });
          this.trackFormError('wrong_credentials');
          if (reason.type === 'USER_SELF_DELETE') {
            this.$emit('switch-mode', 'forgot', { email });
            return;
          }
        }

        const firstErr = data && data.errors && Object.values(data.errors)[0];
        this.submitError =
          (data && data.message) || (Array.isArray(firstErr) && firstErr[0]) || this.t('login_err');
        this.trackFormError('wrong_credentials');
      }
    },
  },
};
</script>
